import { useApp } from '@/contexts';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { PageHeading } from '@/components/layout';
import type { Credential, Environment, IntegrationProvider } from '@/types';

import { useGetIntegrationTypesQuery } from '../api/environmentsApi';
import { useContentfulConnectors } from '../hooks/useContentfulConnectors';
import { useContentfulRecipeTemplates } from '../hooks/useContentfulRecipeTemplates';
import { normalizeCredentialType } from '../utils/credentialTypeMapping';
import { validateConnectors } from '../utils/validateConnectors';
import { ConnectorForm } from './ConnectorForm';
import { ConnectorSelector } from './ConnectorSelector';
import { ConnectorsList } from './ConnectorsList';
import { EmailMonitoring } from './EmailMonitoring';
import { EnvironmentVariables } from './EnvironmentVariables';
import { MetadataExplorer } from './MetadataExplorer';
import { MisconfiguredConnectorModal } from './MisconfiguredConnectorModal';

/**
 * View state machine matching legacy dataProtect Connector.js:
 *
 * list          → Default: shows connections table + buttons
 * selectSource  → ConnectorSelector filtered to readers (Add Source / Connections Wizard step 1)
 * selectDest    → ConnectorSelector filtered to writers (Add Destination / Connections Wizard step 2)
 * addConnector  → ConnectorForm for a new connector (after selection)
 * editConnector → ConnectorForm for editing an existing credential (after row click)
 */
type ViewState = 'list' | 'selectSource' | 'selectDest' | 'addConnector' | 'editConnector';

interface EnvironmentDetailViewProps {
  environment: Environment;
  tenantId: string;
  credentials: Credential[];
  onBack: () => void;
  /** Route param: connector name for deep-linking to edit form */
  initialConnectorName?: string;
  /** Route param: connection key for deep-linking to edit form */
  initialConnectionKey?: string;
  /** Route param: show add source selector */
  initialAddSource?: boolean;
  /** Route param: show add destination selector */
  initialAddDest?: boolean;
  /** Route param: provider type when adding */
  initialProviderType?: string;
  /** Route param: wizard step number */
  initialWizardStep?: number | null;
}

/**
 * Full-page detailed view for a single environment
 * Replaces the list view when an environment is selected (matches original dataProtect)
 */
export const EnvironmentDetailView = ({
  environment,
  tenantId,
  credentials,
  onBack: _onBack,
  initialConnectorName,
  initialConnectionKey,
  initialAddSource,
  initialAddDest,
  initialProviderType,
  initialWizardStep,
}: EnvironmentDetailViewProps) => {
  const { appId } = useApp();
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<ViewState>('list');
  const [selectedProvider, setSelectedProvider] = useState<IntegrationProvider | null>(null);
  const [selectedCredential, setSelectedCredential] = useState<Credential | null>(null);
  const [isWizard, setIsWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1);
  /** Tracks whether user is adding a source or destination (for form header label) */
  const [addMode, setAddMode] = useState<'source' | 'dest' | null>(null);
  const initialOpenDone = useRef(false);
  const [modalShown, setModalShown] = useState(false);

  const { data: providers = [] } = useGetIntegrationTypesQuery();

  // Validate connectors for misconfiguration
  const misconfiguredConnectors = useMemo(() => {
    return validateConnectors(credentials);
  }, [credentials]);

  const handleModalClose = () => {
    setModalShown(true);
  };

  // Reset initialOpenDone when URL parameters change (navigating between different views)
  useEffect(() => {
    initialOpenDone.current = false;
  }, [
    initialConnectorName,
    initialConnectionKey,
    initialAddSource,
    initialAddDest,
    initialProviderType,
    initialWizardStep,
  ]);

  // Auto-open edit form when route params are present (deep-link to edit existing connection)
  useEffect(() => {
    if (
      initialConnectorName &&
      initialConnectionKey &&
      providers.length > 0 &&
      credentials.length > 0 &&
      !initialOpenDone.current
    ) {
      initialOpenDone.current = true;
      const cred = credentials.find(
        (c) => c.providerLabel === initialConnectorName && c.key === initialConnectionKey
      );
      let providerConfig = cred ? providers.find((p) => p.type === cred.providerLabel) : undefined;
      if (!providerConfig && cred) {
        const normalizedType = normalizeCredentialType(cred.credentialType);
        if (normalizedType) {
          providerConfig = providers.find((p) => p.type === normalizedType);
        }
      }
      if (cred && providerConfig) {
        setSelectedCredential(cred);
        setSelectedProvider(providerConfig);
        setViewState('editConnector');
      }
    }
  }, [initialConnectorName, initialConnectionKey, providers, credentials]);

  // Auto-open add source/destination selector or form based on URL
  useEffect(() => {
    if (!initialOpenDone.current && providers.length > 0) {
      // Wizard mode
      if (initialWizardStep) {
        initialOpenDone.current = true;
        setIsWizard(true);
        setWizardStep(initialWizardStep as 1 | 2 | 3);
        if (initialWizardStep === 3) {
          setAddMode(null);
          setViewState('list');
        } else if (initialProviderType) {
          const provider = providers.find((p) => p.type === initialProviderType);
          setAddMode(initialWizardStep === 1 ? 'source' : 'dest');
          if (provider) {
            setSelectedProvider(provider);
            setViewState('addConnector');
          } else {
            setViewState(initialWizardStep === 1 ? 'selectSource' : 'selectDest');
          }
        } else {
          setAddMode(initialWizardStep === 1 ? 'source' : 'dest');
          setViewState(initialWizardStep === 1 ? 'selectSource' : 'selectDest');
        }
        return;
      }

      // Add source - with or without provider type
      if (initialAddSource) {
        initialOpenDone.current = true;
        setIsWizard(false);
        setAddMode('source');

        if (initialProviderType) {
          // Show form for specific provider
          const provider = providers.find((p) => p.type === initialProviderType);
          if (provider) {
            setSelectedProvider(provider);
            setViewState('addConnector');
          }
        } else {
          // Show selector
          setViewState('selectSource');
        }
        return;
      }

      // Add destination - with or without provider type
      if (initialAddDest) {
        initialOpenDone.current = true;
        setIsWizard(false);
        setAddMode('dest');

        if (initialProviderType) {
          // Show form for specific provider
          const provider = providers.find((p) => p.type === initialProviderType);
          if (provider) {
            setSelectedProvider(provider);
            setViewState('addConnector');
          }
        } else {
          // Show selector
          setViewState('selectDest');
        }
        return;
      }
    }
  }, [initialAddSource, initialAddDest, initialProviderType, initialWizardStep, providers]);

  // ─── Button handlers matching legacy dataProtect envBars.js ───

  /**
   * "Add Source" button — shows ConnectorSelector filtered to readers
   * Matches envBars.js: showNewConnectorComponent("reader")
   */
  const handleAddSource = () => {
    setIsWizard(false);
    setAddMode('source');
    setSelectedProvider(null);
    setSelectedCredential(null);
    setViewState('selectSource');
    // Update URL for deep-linking
    navigate(`/a/${appId}/env/${environment.id}/add-source`);
  };

  /**
   * "Add Destination" button — shows ConnectorSelector filtered to writers
   * Matches envBars.js: showNewConnectorComponent("writer")
   */
  const handleAddDestination = () => {
    setIsWizard(false);
    setAddMode('dest');
    setSelectedProvider(null);
    setSelectedCredential(null);
    setViewState('selectDest');
    // Update URL for deep-linking
    navigate(`/a/${appId}/env/${environment.id}/add-destination`);
  };

  /**
   * "Connections Wizard" button — starts wizard flow (source → destination → recipe)
   * Matches envBars.js: showNewConnectorComponent("reader") + newUserExperience + isWizard
   */
  const handleConnectionsWizard = () => {
    setIsWizard(true);
    setWizardStep(1);
    setAddMode('source');
    setSelectedProvider(null);
    setSelectedCredential(null);
    setViewState('selectSource');
    // Update URL for wizard mode
    navigate(`/a/${appId}/env/${environment.id}/wizard/step-1`);
  };

  /**
   * Row click on a credential — navigates to edit form URL
   * Matches Connector.js showConnectorDetails()
   * URL: /a/:appId/env/:envId/connections/:connectorName/connectionKeys/:connectionKey
   */
  const handleCredentialClick = (credential: Credential) => {
    // Match by providerLabel first (same as old UI Connector.js line 851-856),
    // then fall back to credentialType with normalization for OAuth types.
    let providerConfig = providers.find((p) => p.type === credential.providerLabel);
    if (!providerConfig) {
      const normalizedType = normalizeCredentialType(credential.credentialType);
      if (!normalizedType) return;
      providerConfig = providers.find((p) => p.type === normalizedType);
      if (!providerConfig) return;
    }

    setSelectedCredential(credential);
    setSelectedProvider(providerConfig);
    setIsWizard(false);
    setAddMode(null);
    setViewState('editConnector');

    // Update URL to the connection edit route (deep-linkable)
    navigate(
      `/a/${appId}/env/${environment.id}/connections/${credential.providerLabel}/connectionKeys/${credential.key}`
    );
  };

  /**
   * Connector selected from ConnectorSelector grid
   * Matches ConnectorBar.js changeConnector() → shows ConnectorSection
   */
  const handleConnectorSelected = (provider: IntegrationProvider) => {
    setSelectedProvider(provider);
    setSelectedCredential(null);
    setViewState('addConnector');

    // Update URL based on wizard mode or regular add mode
    if (isWizard) {
      // In wizard mode, stay on the wizard step URL
      navigate(`/a/${appId}/env/${environment.id}/wizard/step-${wizardStep}/${provider.type}`);
    } else {
      // Regular add mode
      const mode = addMode === 'dest' ? 'add-destination' : 'add-source';
      navigate(`/a/${appId}/env/${environment.id}/${mode}/${provider.type}`);
    }
  };

  /**
   * Skip button in wizard mode
   * Step 1: Skip source → go to destination (step 2)
   * Step 2: Skip destination → go to recipe creation (step 3)
   */
  const handleWizardSkip = () => {
    if (!isWizard) return;

    if (wizardStep === 1) {
      // Skip source → go to step 2 (destination)
      setWizardStep(2);
      setAddMode('dest');
      setSelectedProvider(null);
      setSelectedCredential(null);
      setViewState('selectDest');
      navigate(`/a/${appId}/env/${environment.id}/wizard/step-2`);
    } else if (wizardStep === 2) {
      // Skip destination → go to step 3 (create recipe)
      setWizardStep(3);
      setAddMode(null);
      setSelectedProvider(null);
      setSelectedCredential(null);
      setViewState('list'); // Will render step 3 content via wizardStep === 3 check
      navigate(`/a/${appId}/env/${environment.id}/wizard/step-3`);
    }
  };

  /**
   * Cancel from ConnectorSelector → back to list
   */
  const handleSelectorCancel = () => {
    setIsWizard(false);
    setViewState('list');
    // Navigate back to env detail URL
    navigate(`/a/${appId}/env/${environment.id}`);
  };

  /**
   * Cancel from ConnectorForm → back to selector (if adding) or list (if editing)
   */
  const handleFormCancel = () => {
    if (viewState === 'editConnector') {
      setViewState('list');
      // Navigate back to the env detail URL (remove connection params)
      navigate(`/a/${appId}/env/${environment.id}`);
    } else if (isWizard) {
      // In wizard mode, go back to the selector for the current step
      setViewState(wizardStep === 1 ? 'selectSource' : 'selectDest');
      navigate(`/a/${appId}/env/${environment.id}/wizard/step-${wizardStep}`);
    } else {
      // Go back to selector
      const mode = addMode === 'dest' ? 'add-destination' : 'add-source';
      setViewState(selectedProvider?.readerName === 'Reader' ? 'selectSource' : 'selectDest');
      navigate(`/a/${appId}/env/${environment.id}/${mode}`);
    }
  };

  /**
   * Credential saved (add or edit) → back to list or next wizard step
   * Matches Connector.js handleCheckData() for wizard flow
   */
  const handleCredentialSaved = () => {
    if (isWizard && wizardStep === 1) {
      // Wizard step 1 complete → move to step 2 (destination)
      setWizardStep(2);
      setAddMode('dest');
      setSelectedProvider(null);
      setSelectedCredential(null);
      setViewState('selectDest');
      // Update URL to reflect step 2
      navigate(`/a/${appId}/env/${environment.id}/wizard/step-2`);
    } else if (isWizard && wizardStep === 2) {
      // Wizard step 2 complete → move to step 3 (create recipe)
      setWizardStep(3);
      setAddMode(null);
      setSelectedProvider(null);
      setSelectedCredential(null);
      setViewState('list'); // Will render step 3 content via wizardStep === 3 check
      navigate(`/a/${appId}/env/${environment.id}/wizard/step-3`);
    } else {
      // Non-wizard → back to list
      setIsWizard(false);
      setViewState('list');
      // Navigate back to env detail URL
      navigate(`/a/${appId}/env/${environment.id}`);
    }
  };

  // Existing credential types for LingkWebSocket exclusion logic
  const existingCredentialTypes = credentials.map((c) => c.credentialType);

  // ─── Render based on view state ───

  // ConnectorSelector view (Add Source / Add Destination / Wizard)
  if (viewState === 'selectSource' || viewState === 'selectDest') {
    const filter = viewState === 'selectSource' ? 'reader' : 'writer';
    const title =
      viewState === 'selectSource' ? 'Select a Source Connector' : 'Select a Destination Connector';
    const pageTitle =
      viewState === 'selectSource' ? 'Add Source Connector' : 'Add Destination Connector';
    const subtitle =
      viewState === 'selectSource'
        ? 'Choose a source system to read data from'
        : 'Choose a destination system to write data to';

    return (
      <Box sx={{ width: '100%' }}>
        {/* Page Heading */}
        <PageHeading title={pageTitle} subtitle={subtitle} />

        {/* Wizard progress indicator */}
        {isWizard && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              px: 1,
            }}
          >
            <WizardStep step={1} label="Connect Source" active={wizardStep === 1} />
            <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
            <WizardStep step={2} label="Connect Destination" active={wizardStep === 2} />
            <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
            <WizardStep step={3} label="Create a Recipe" active={wizardStep === 3} />
          </Box>
        )}

        {/* Breadcrumb navigation: envName > Connections > title */}
        <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 18 }} />} sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            onClick={handleSelectorCancel}
            sx={{ fontSize: '14px', color: '#54698d', cursor: 'pointer' }}
          >
            {environment.name}
          </Link>
          <Link
            component="button"
            underline="hover"
            onClick={handleSelectorCancel}
            sx={{ fontSize: '14px', color: '#54698d', cursor: 'pointer' }}
          >
            Connections
          </Link>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>{title}</Typography>
        </Breadcrumbs>

        <ConnectorSelector
          filter={filter}
          onSelect={handleConnectorSelected}
          existingCredentialTypes={existingCredentialTypes}
          showSkip={isWizard}
          onSkip={handleWizardSkip}
        />
      </Box>
    );
  }

  // ConnectorForm view (Add or Edit)
  if ((viewState === 'addConnector' || viewState === 'editConnector') && selectedProvider) {
    return (
      <Box sx={{ width: '100%' }}>
        <ConnectorForm
          provider={selectedProvider}
          tenantId={tenantId}
          envId={environment.id}
          envName={environment.name}
          existingCredential={selectedCredential}
          existingCredentials={credentials}
          onCancel={handleFormCancel}
          onSaved={handleCredentialSaved}
          formMode={(() => {
            if (viewState === 'editConnector') return 'edit';
            return addMode === 'dest' ? 'addDest' : 'addSource';
          })()}
          isWizard={isWizard}
          wizardStep={wizardStep}
        />

        {/* Fixed Skip button for wizard mode - matches dataProtect Connector.js */}
        {isWizard && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleWizardSkip}
            sx={{
              position: 'fixed',
              left: '50%',
              bottom: '16px',
              transform: 'translateX(-50%)',
              width: '65px',
              height: '30px',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 'normal',
              borderColor: '#ccc',
              color: '#666',
              backgroundColor: '#fff',
              zIndex: 1000,
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Skip
          </Button>
        )}
      </Box>
    );
  }

  // ─── Wizard Step 3: Create a recipe (inline, same page as steps 1 & 2) ───
  // Matches legacy dataProtect newTransfomerIntg.js
  if (isWizard && wizardStep === 3) {
    const connectorCredentials = credentials.filter(
      (c) => c.credentialType !== 'EnvironmentVariables' && c.credentialType !== 'AdapterSecret'
    );
    const hasConnections = connectorCredentials.length > 0;

    return (
      <Box sx={{ width: '100%' }}>
        {/* Page Heading */}
        <PageHeading
          title="Create a Recipe"
          subtitle="Connect your systems and transform data with a recipe"
        />

        {/* Wizard progress indicator */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            px: 1,
          }}
        >
          <WizardStep step={1} label="Connect Source" active={false} />
          <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
          <WizardStep step={2} label="Connect Destination" active={false} />
          <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
          <WizardStep step={3} label="Create a Recipe" active />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 6,
          }}
        >
          {hasConnections ? (
            <WizardStep3Content
              connectorCredentials={connectorCredentials}
              appId={appId}
              environment={environment}
              navigate={navigate}
              setIsWizard={setIsWizard}
              setWizardStep={setWizardStep}
              setViewState={setViewState}
            />
          ) : (
            <Typography sx={{ fontSize: '14px' }}>
              No integration available. Please configure either source or destination connection
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // ─── Default: List view ───
  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Heading */}
      <PageHeading
        title={environment.name}
        subtitle="Manage data connectors and environment configuration"
      />

      {/* Connections Section */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 'bold', px: 0.75 }}>
            Connections
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleConnectionsWizard}
              sx={{
                fontSize: '13px',
                textTransform: 'none',
                height: 32,
                fontWeight: 'normal',
                borderColor: '#ccc',
                color: '#333',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Connections Wizard
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddSource}
              sx={{
                fontSize: '13px',
                textTransform: 'none',
                height: 32,
                fontWeight: 'normal',
                borderColor: '#ccc',
                color: '#333',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Add Source
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddDestination}
              sx={{
                fontSize: '13px',
                textTransform: 'none',
                height: 32,
                fontWeight: 'normal',
                borderColor: '#ccc',
                color: '#333',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Add Destination
            </Button>
          </Box>
        </Box>

        {credentials.filter(
          (c) => c.credentialType !== 'AdapterSecret' && c.credentialType !== 'EnvironmentVariables'
        ).length > 0 ? (
          <ConnectorsList
            tenantId={tenantId}
            envName={environment.name}
            envId={environment.id}
            credentials={credentials || []}
            onCredentialClick={handleCredentialClick}
            misconfiguredConnectors={misconfiguredConnectors}
          />
        ) : (
          <Box sx={{ ml: 1.75, my: 2 }}>
            <Typography sx={{ fontSize: '13px' }}>
              No Connectors found for this Environment
            </Typography>
          </Box>
        )}
      </Box>

      {/* Environment Variables Section */}
      <Box sx={{ mb: 3 }}>
        <EnvironmentVariables
          tenantId={tenantId}
          envName={environment.name}
          envId={environment.id}
          credentials={credentials || []}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* MetaScore UI Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            mb: 2,
            mt: 2,
            display: 'inline-block',
          }}
        >
          MetaScore UI
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const metaScoreUIUrl = 'http://localhost:3001';
              window.open(
                `${metaScoreUIUrl}/lingk-meta-score?tenantId=${tenantId}&projectId=${appId}&environmentId=${environment.id}`,
                '_blank'
              );
            }}
            sx={{ textTransform: 'none' }}
          >
            Open MetaScore UI
          </Button>
          <Typography sx={{ mt: 1, color: '#666', fontSize: 14 }}>
            Launch the MetaScore UI to manage your data dictionaries and transformations.
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Notifications (Email Monitoring) Section */}
      <EmailMonitoring tenantId={tenantId} envId={environment.id} />

      <Divider sx={{ my: 2 }} />

      {/* Metadata Explorer Section */}
      <MetadataExplorer
        tenantId={tenantId}
        envName={environment.name}
        credentials={credentials || []}
      />

      {/* Misconfigured Connector Modal */}
      {!modalShown && (
        <MisconfiguredConnectorModal
          misconfiguredConnectors={misconfiguredConnectors}
          onClose={handleModalClose}
        />
      )}
    </Box>
  );
};

/**
 * Wizard progress step indicator
 * Matches legacy dataProtect Connector.js wizard progress bar
 */
const WizardStep = ({ step, label, active }: { step: number; label: string; active: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: active ? '#00853E' : '#e0e0e0',
        color: active ? '#fff' : '#666',
      }}
    >
      {step}
    </Box>
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: active ? 600 : 400,
        color: active ? '#00853E' : '#666',
      }}
    >
      {label}
    </Typography>
  </Box>
);

/**
 * Wizard Step 3 content - Recipe suggestions
 * Matches legacy dataProtect newTransfomerIntg.js
 * - Shows connector logos
 * - Lists configured connectors
 * - Shows suggested recipes from Contentful (filtered by source/dest)
 */
interface WizardStep3ContentProps {
  connectorCredentials: Credential[];
  appId: string;
  environment: Environment;
  navigate: ReturnType<typeof useNavigate>;
  setIsWizard: (isWizard: boolean) => void;
  setWizardStep: Dispatch<SetStateAction<1 | 2 | 3>>;
  setViewState: (state: ViewState) => void;
}

const WizardStep3Content = ({
  connectorCredentials,
  appId,
  environment,
  navigate,
  setIsWizard,
  setWizardStep,
  setViewState,
}: WizardStep3ContentProps) => {
  const { connectors: contentfulConnectors } = useContentfulConnectors();
  const { getSuggestedTemplates } = useContentfulRecipeTemplates();
  const { data: providers } = useGetIntegrationTypesQuery();

  // Find source and destination connectors
  const sourceCredential = connectorCredentials.find((c) => c.wizard === 'isSource');
  const destCredential = connectorCredentials.find((c) => c.wizard === 'isDestination');

  // Get connector logos from Contentful
  const getConnectorLogo = (credentialType: string): string | undefined => {
    const contentfulConnector = contentfulConnectors.find(
      (c) => c.fields.providerType === credentialType
    );
    return contentfulConnector?.fields.logo?.fields?.file?.url;
  };

  // Get connector display name
  const getConnectorName = (credential: Credential): string => {
    if (credential.connectionName) return credential.connectionName;
    if (credential.providerLabel) return credential.providerLabel;

    // Fallback to provider type from API
    const provider = providers?.find((p) => p.type === credential.credentialType);
    return provider?.label || credential.credentialType;
  };

  // Get suggested recipe templates
  const suggestedTemplates = getSuggestedTemplates(
    sourceCredential?.credentialType,
    destCredential?.credentialType
  );

  const handleCreateRecipeFromTemplate = (templateName: string, templateContent: string) => {
    // Navigate to recipe editor with template pre-loaded
    navigate(`/a/${appId}/tf/new`, {
      state: {
        templateName,
        templateContent,
        environmentId: environment.id,
      },
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Section Heading */}
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#333',
          mb: 3,
          textAlign: 'left',
        }}
      >
        Your Connected Systems
      </Typography>

      {/* Connector Logos Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
          gap: 2,
          mb: 4,
        }}
      >
        {connectorCredentials.map((credential) => {
          const logo = getConnectorLogo(credential.credentialType);
          const connectorName = getConnectorName(credential);

          return (
            <Tooltip key={credential.key} title={connectorName} arrow placement="top">
              <Box
                onClick={() => {
                  const connectorType = credential.credentialType.toLowerCase();
                  navigate(`/a/${appId}/tf/new?connectorType=${connectorType}`);
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  padding: 1.5,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#00853E',
                    backgroundColor: '#f5faf7',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                {logo ? (
                  <Box
                    component="img"
                    src={logo}
                    alt={connectorName}
                    sx={{
                      height: 60,
                      width: 60,
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 60,
                      width: 60,
                      borderRadius: 1,
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: '#666',
                      textAlign: 'center',
                      padding: 0.5,
                    }}
                  >
                    {connectorName}
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: '#666',
                    textAlign: 'center',
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                  }}
                >
                  {connectorName}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Recipe Suggestions */}
      <Typography sx={{ fontSize: '15px', fontWeight: 'bold', mt: 3, mb: 1 }}>
        Getting Start with one of these recipes
      </Typography>

      {suggestedTemplates.length > 0 ? (
        <List
          sx={{
            maxWidth: 600,
            mx: 'auto',
            mb: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
          }}
        >
          {suggestedTemplates.map((template, index) => (
            <ListItemButton
              key={index}
              onClick={() => handleCreateRecipeFromTemplate(template.title, template.template)}
              sx={{
                borderBottom: index < suggestedTemplates.length - 1 ? '1px solid #e0e0e0' : 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 133, 62, 0.08)',
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                    {template.title}
                  </Typography>
                }
                secondary={
                  template.description && (
                    <Typography sx={{ fontSize: '12.5px', color: '#666' }}>
                      {template.description}
                    </Typography>
                  )
                }
              />
            </ListItemButton>
          ))}
        </List>
      ) : (
        <Typography sx={{ fontSize: '14px', color: '#666', mb: 2 }}>
          No recipe suggestions available
        </Typography>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(`/a/${appId}/tf`)}
          sx={{
            textTransform: 'none',
            borderColor: '#ccc',
            color: '#333',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Browse recipes
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setIsWizard(false);
            setWizardStep(1);
            setViewState('list');
            navigate(`/a/${appId}/env/${environment.id}`);
          }}
          sx={{
            textTransform: 'none',
            borderColor: '#ccc',
            color: '#333',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Go to environment
        </Button>
      </Box>
    </Box>
  );
};
